import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { AppLayout } from "./layouts/AppLayout";
import { ErrorBoundary } from "./components/ErrorBoundary";

/** 路由切换滚到顶部。营销页实际滚动容器是 #root（见 index.css :has(.lp/.hv)），不是 window。 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const toTop = () => {
      const root = document.getElementById("root");
      if (root) root.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
    };

    if (hash) {
      const id = decodeURIComponent(hash.replace(/^#/, ""));
      const run = () => {
        const el = id ? document.getElementById(id) : null;
        if (!el) {
          toTop();
          return;
        }
        const root = document.getElementById("root");
        const nav = document.querySelector(".lp-nav") as HTMLElement | null;
        const offset = (nav?.offsetHeight ?? 64) + 10;
        if (root) {
          const top =
            el.getBoundingClientRect().top -
            root.getBoundingClientRect().top +
            root.scrollTop -
            offset;
          root.scrollTo({ top: Math.max(0, top), behavior: "auto" });
        } else {
          el.scrollIntoView();
        }
      };
      // 等懒加载页挂载后再找锚点
      const raf = requestAnimationFrame(run);
      const t = window.setTimeout(run, 80);
      const t2 = window.setTimeout(run, 280);
      return () => {
        cancelAnimationFrame(raf);
        window.clearTimeout(t);
        window.clearTimeout(t2);
      };
    }

    toTop();
    const raf = requestAnimationFrame(toTop);
    const t = window.setTimeout(toTop, 50);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [pathname, hash]);
  return null;
}

// ─── Code-split page imports ─────────────────────────────
const LandingPage = lazy(() => import("./pages/LandingPage").then(m => ({ default: m.LandingPage })));
const EcosystemPage = lazy(() =>
  import("./pages/marketing/EcosystemPage").then((m) => ({ default: m.default })),
);
const CapabilitiesPage = lazy(() =>
  import("./pages/marketing/CapabilitiesPage").then((m) => ({ default: m.default })),
);
const WorkflowPage = lazy(() =>
  import("./pages/marketing/WorkflowPage").then((m) => ({ default: m.default })),
);
const PricingPage = lazy(() =>
  import("./pages/marketing/PricingPage").then((m) => ({ default: m.default })),
);
const SupportPage = lazy(() =>
  import("./pages/marketing/SupportPage").then((m) => ({ default: m.default })),
);
const LoginPage = lazy(() => import("./pages/LoginPage").then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("./pages/RegisterPage").then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })),
);
const OwnerElevatedPage = lazy(() =>
  import("./pages/OwnerElevatedPage").then((m) => ({ default: m.OwnerElevatedPage })),
);
const OwnerProjectAuthPage = lazy(() =>
  import("./pages/OwnerProjectAuthPage").then((m) => ({
    default: m.OwnerProjectAuthPage,
  })),
);
const OwnerGeekDebugPage = lazy(() =>
  import("./pages/OwnerGeekDebugPage").then((m) => ({
    default: m.OwnerGeekDebugPage,
  })),
);
const GeekDebugPage = lazy(() =>
  import("./pages/GeekDebugPage").then((m) => ({ default: m.GeekDebugPage })),
);
const CustomerH5HomePage = lazy(() =>
  import("./pages/CustomerH5HomePage").then((m) => ({
    default: m.CustomerH5HomePage,
  })),
);
const CustomerLoginPage = lazy(() =>
  import("./pages/CustomerLoginPage").then((m) => ({
    default: m.CustomerLoginPage,
  })),
);
const CustomerHomePage = lazy(() =>
  import("./pages/CustomerHomePage").then((m) => ({
    default: m.CustomerHomePage,
  })),
);
const CustomerProjectPage = lazy(() =>
  import("./pages/CustomerProjectPage").then((m) => ({
    default: m.CustomerProjectPage,
  })),
);
const DashboardPage = lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const CustomersPage = lazy(() => import("./pages/CustomersPage").then(m => ({ default: m.CustomersPage })));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage").then(m => ({ default: m.ProjectDetailPage })));
const TicketsPage = lazy(() => import("./pages/TicketsPage").then(m => ({ default: m.TicketsPage })));
const SupportRequestsPage = lazy(() =>
  import("./pages/SupportRequestsPage").then((m) => ({ default: m.default })),
);
const AdminSupportRequestsPage = lazy(() =>
  import("./pages/AdminSupportRequestsPage").then((m) => ({
    default: m.default,
  })),
);
const UsersPage = lazy(() => import("./pages/UsersPage").then(m => ({ default: m.UsersPage })));
const AuditPage = lazy(() => import("./pages/AuditPage").then(m => ({ default: m.AuditPage })));
const AgentPage = lazy(() => import("./pages/AgentPage").then(m => ({ default: m.AgentPage })));
const BillingPage = lazy(() => import("./pages/BillingPage").then(m => ({ default: m.BillingPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const ChangePasswordPage = lazy(() =>
  import("./pages/ChangePasswordPage").then((m) => ({ default: m.default })),
);
const RolesPage = lazy(() => import("./pages/RolesPage").then(m => ({ default: m.RolesPage })));
const SiteConfigPage = lazy(() =>
  import("./pages/SiteConfigPage").then((m) => ({ default: m.SiteConfigPage })),
);
const DevicesPage = lazy(() => import("./pages/DevicesPage").then(m => ({ default: m.DevicesPage })));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage").then(m => ({ default: m.ResourcesPage })));
const CertificationPage = lazy(() =>
  import("./pages/CertificationPage").then((m) => ({ default: m.CertificationPage })),
);
const AuthRecordsPage = lazy(() => import("./pages/AuthRecordsPage").then(m => ({ default: m.AuthRecordsPage })));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const AdminTenantsPage = lazy(() => import("./pages/AdminTenantsPage").then(m => ({ default: m.default })));
const AdminAgentPage = lazy(() =>
  import("./pages/AdminAgentPage").then((m) => ({ default: m.default })),
);
const AdminIntegrationMirrorsPage = lazy(() =>
  import("./pages/AdminIntegrationMirrorsPage").then((m) => ({
    default: m.default,
  })),
);
const AdminDeviceCategoryMapsPage = lazy(() =>
  import("./pages/AdminDeviceCategoryMapsPage").then((m) => ({
    default: m.default,
  })),
);
const AdminAiCapabilityPage = lazy(() =>
  import("./pages/AdminAiCapabilityPage").then((m) => ({ default: m.default })),
);
const AdminOpsProjectsPage = lazy(() =>
  import("./pages/AdminOpsProjectsPage").then((m) => ({ default: m.default })),
);
const AdminApiConfigPage = lazy(() =>
  import("./pages/AdminApiConfigPage").then((m) => ({ default: m.default })),
);
const AdminAqaraConfigPage = lazy(() =>
  import("./pages/AdminAqaraConfigPage").then((m) => ({ default: m.default })),
);
const AdminWechatConfigPage = lazy(() =>
  import("./pages/AdminWechatConfigPage").then((m) => ({ default: m.default })),
);
const AdminCognitivePage = lazy(() =>
  import("./pages/AdminCognitivePage").then((m) => ({
    default: m.default,
  })),
);
const AdminDeliveryStandardsPage = lazy(() =>
  import("./pages/AdminDeliveryStandardsPage").then((m) => ({
    default: m.default,
  })),
);
const AdminMijiaHubCapabilityPage = lazy(() =>
  import("./pages/AdminMijiaHubCapabilityPage").then((m) => ({
    default: m.default,
  })),
);

function LegacyAqaraDebugRedirect() {
  const { id, did } = useParams();
  if (!id) return <Navigate to="/projects" replace />;
  if (did) {
    return (
      <Navigate
        to={`/projects/${encodeURIComponent(id)}/debug/devices/${encodeURIComponent(`aq:${did}`)}`}
        replace
        state={{ debugNav: "devices" }}
      />
    );
  }
  return <Navigate to={`/projects/${encodeURIComponent(id)}/debug`} replace />;
}

function AdminAiFactorsRoute() {
  return <AdminAiCapabilityPage view="factors" />;
}

function AdminAiAlgorithmsRoute() {
  return <AdminAiCapabilityPage view="algorithms" />;
}

// ─── Route Guards ────────────────────────────────────────
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function PlatformAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (role !== "platform_admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}


/** Tenant governance: integrator_admin only; platform_admin redirected to /admin/*. */
function IntegratorAdminRoute({
  children,
  platformPath,
}: {
  children: React.ReactNode;
  platformPath: string;
}) {
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (role === "platform_admin") {
    return <Navigate to={platformPath} replace />;
  }
  if (role !== "integrator_admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

/** Alert-collection tickets / install guide: tenant ops only; platform admin stays out. */
function TenantOpsRoute({
  children,
  platformPath = "/dashboard",
}: {
  children: React.ReactNode;
  platformPath?: string;
}) {
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (role === "platform_admin") {
    return <Navigate to={platformPath} replace />;
  }
  return <>{children}</>;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

function HomeRoute() {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
}

// ─── Loading Fallback ────────────────────────────────────
function PageLoader() {
  return (
    <div className="page-loader">
      <span>加载中…</span>
    </div>
  );
}

function LegacyCustomersRedirect() {
  const location = useLocation();
  const target =
    location.pathname.replace(/^\/customers/, "/projects") +
    location.search +
    location.hash;
  return <Navigate to={target} replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <ScrollToTop />
        <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/ecosystem" element={<EcosystemPage />} />
        <Route path="/capabilities" element={<CapabilitiesPage />} />
        <Route path="/workflow" element={<WorkflowPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
        <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
        <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
        <Route path="/reset-password" element={<PublicOnlyRoute><ResetPasswordPage /></PublicOnlyRoute>} />
        <Route path="/owner/elevated" element={<OwnerElevatedPage />} />
        <Route path="/owner/project-auth" element={<OwnerProjectAuthPage />} />
        <Route path="/owner/geek-debug" element={<OwnerGeekDebugPage />} />
        <Route path="/c" element={<CustomerH5HomePage />} />
        <Route path="/c/login" element={<CustomerLoginPage />} />
        <Route path="/c/home" element={<CustomerHomePage />} />
        <Route path="/c/projects/:projectId" element={<CustomerProjectPage />} />
        <Route path="/c/elevated" element={<OwnerElevatedPage />} />
        <Route path="/c/project-auth" element={<OwnerProjectAuthPage />} />
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/projects" element={<CustomersPage />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />
          <Route path="/projects/:id/debug" element={<ProjectDetailPage />} />
          <Route
            path="/projects/:id/debug/devices/:deviceId"
            element={<ProjectDetailPage />}
          />
          <Route path="/projects/:id/geek-debug" element={<GeekDebugPage />} />
          <Route path="/projects/:id/aqara" element={<LegacyAqaraDebugRedirect />} />
          <Route path="/projects/:id/aqara/devices/:did" element={<LegacyAqaraDebugRedirect />} />
          <Route path="/customers" element={<Navigate to="/projects" replace />} />
          <Route path="/customers/*" element={<LegacyCustomersRedirect />} />
          <Route path="/remote" element={<Navigate to="/projects?tab=pending_support" replace />} />
          <Route path="/devices" element={<DevicesPage />} />
          <Route
            path="/users"
            element={
              <IntegratorAdminRoute platformPath="/admin/system-users">
                <UsersPage />
              </IntegratorAdminRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <IntegratorAdminRoute platformPath="/admin/roles">
                <RolesPage />
              </IntegratorAdminRoute>
            }
          />
          <Route
            path="/audit"
            element={
              <IntegratorAdminRoute platformPath="/admin/audit">
                <AuditPage />
              </IntegratorAdminRoute>
            }
          />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/certification" element={<CertificationPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/password" element={<ChangePasswordPage />} />
          <Route
            path="/site-config"
            element={<Navigate to="/admin/site-config" replace />}
          />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route
            path="/alerts"
            element={
              <TenantOpsRoute>
                <Navigate to="/tickets?view=instances" replace />
              </TenantOpsRoute>
            }
          />
          <Route
            path="/tickets"
            element={
              <TenantOpsRoute>
                <TicketsPage />
              </TenantOpsRoute>
            }
          />
          <Route
            path="/support-requests"
            element={
              <TenantOpsRoute>
                <SupportRequestsPage />
              </TenantOpsRoute>
            }
          />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/ai" element={<Navigate to="/projects" replace />} />
          <Route path="/health" element={<Navigate to="/dashboard" replace />} />
          <Route path="/auth-records" element={<AuthRecordsPage />} />
          <Route path="/diff/agent" element={<AgentPage />} />
          <Route path="/aqara" element={<Navigate to="/projects" replace />} />
          <Route path="/aqara/*" element={<Navigate to="/projects" replace />} />

          {/* Platform Admin Routes (nested in AppLayout, guarded by PlatformAdminRoute) */}
          <Route path="/admin/tenants" element={<PlatformAdminRoute><AdminTenantsPage /></PlatformAdminRoute>} />
          <Route
            path="/admin/support-requests"
            element={
              <PlatformAdminRoute>
                <AdminSupportRequestsPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/projects"
            element={
              <PlatformAdminRoute>
                <AdminOpsProjectsPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/cognitive"
            element={
              <PlatformAdminRoute>
                <AdminCognitivePage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/delivery-standards"
            element={
              <PlatformAdminRoute>
                <AdminDeliveryStandardsPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/mijia-hub"
            element={
              <PlatformAdminRoute>
                <AdminMijiaHubCapabilityPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/system-users"
            element={
              <PlatformAdminRoute>
                <UsersPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <PlatformAdminRoute>
                <RolesPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <PlatformAdminRoute>
                <AuditPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/operation-logs"
            element={<Navigate to="/admin/audit" replace />}
          />
          <Route
            path="/admin/site-config"
            element={
              <PlatformAdminRoute>
                <SiteConfigPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/agent"
            element={
              <PlatformAdminRoute>
                <AdminAgentPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/agent-releases"
            element={<Navigate to="/admin/agent" replace />}
          />
          <Route
            path="/admin/integration-mirrors"
            element={
              <PlatformAdminRoute>
                <AdminIntegrationMirrorsPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/device-category-maps"
            element={
              <PlatformAdminRoute>
                <AdminDeviceCategoryMapsPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/ai-factors"
            element={
              <PlatformAdminRoute>
                <AdminAiFactorsRoute />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/ai-algorithms"
            element={
              <PlatformAdminRoute>
                <AdminAiAlgorithmsRoute />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/ai-capability"
            element={<Navigate to="/admin/ai-factors" replace />}
          />
          <Route
            path="/admin/api-config"
            element={
              <PlatformAdminRoute>
                <AdminApiConfigPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/api-config/aqara"
            element={
              <PlatformAdminRoute>
                <AdminAqaraConfigPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/api-config/wechat"
            element={
              <PlatformAdminRoute>
                <AdminWechatConfigPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/frontend"
            element={<Navigate to="/admin/site-config" replace />}
          />
          <Route
            path="/admin/aqara-config"
            element={<Navigate to="/admin/api-config/aqara" replace />}
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
